import { ISbStoryData } from '@storyblok/react';
import random from 'lodash/random';
import { ISbComponentType } from 'storyblok-js-client';
import { NavigationRootStoryblok } from 'storyblok-types';
import { components } from 'pages/StoryBlok';
import { ClientPageType } from 'src/app/types';
import { PageType, SiteConfigStories, SiteConfigStoryData } from './types';

export { renderRichText } from '@storyblok/react';

const parseNavigationToCategoriesMap = (header: NavigationRootStoryblok) => {
  /**
   * Extract root categories from header
   * */
  const firstLvl = header?.children ?? [];

  /**
   * Extract flyout columns and their items
   * */
  const secondLvl =
    firstLvl.flatMap((child) =>
      child.flyoutColumns?.flatMap((flyoutColumn) => flyoutColumn?.items),
    ) ?? [];

  /**
   * Extract child categories
   * */
  const thirdLvl = secondLvl.flatMap((column) => column?.children);

  const allCategories = [...firstLvl, ...secondLvl, ...thirdLvl];

  return allCategories.reduce((acc, category) => {
    const url = (
      category?.url?.story?.full_slug || category?.url?.cached_url
    )?.replace(/^\/+/, '');

    if (category?.title && url) {
      /**
       * Transform the name into the correct JSON format
       * */
      const name = category?.title.toLocaleLowerCase().replaceAll(' ', '-');

      return {
        ...acc,
        [name]: url,
      };
    }

    return acc;
  }, {});
};

function parseHeader(header: NavigationRootStoryblok) {
  return {
    ...header,
    children: header.children?.map((c) => ({
      ...c,
      /**
       * Random index of the banners set for the header flyout
       * */
      bannerIdx: random(0, (c.bannersSet?.length || 1) - 1),
    })),
  };
}

export const ParseNavigation = (nav: SiteConfigStoryData[]) => {
  return nav.reduce<SiteConfigStories>((acc, item) => {
    switch (item.content.component) {
      case 'Footer':
        return { ...acc, footer: item.content };
      case 'NavigationRoot':
        return {
          ...acc,
          header: parseHeader(item.content),
          categoriesMap: parseNavigationToCategoriesMap(item.content),
        };
      case 'colors':
        return { ...acc, colors: item.content };
      case 'shippingInfo':
        return { ...acc, shipping: item.content };
      case 'productDetail':
        return { ...acc, productDetail: item.content };
      case 'materialInfo':
        return { ...acc, materialInfo: item.content };
      case 'deliveryInfo':
        return { ...acc, deliveryInfo: item.content };
    }
  }, {});
};

export const isValidDate = (
  start_date?: string,
  expiration_date?: string,
): boolean => {
  const now = Date.now();
  const startTime = start_date ? Date.parse(start_date) : -Infinity;
  const endTime = expiration_date ? Date.parse(expiration_date) : Infinity;

  return startTime < now && now < endTime;
};

export const getPageType = (story: ISbStoryData): PageType => {
  let pageType: PageType = null;

  if (!story?.content?.body) {
    return pageType;
  }

  story.content.body.forEach(
    (item: ISbComponentType<keyof typeof components>) => {
      if (!pageType) {
        switch (item.component) {
          case 'banner-theme-hero':
            pageType = ClientPageType.THEME;
            break;
          case 'productGrid':
            pageType = ClientPageType.CATEGORY;
            break;
          case 'ListicleBlock':
            pageType = ClientPageType.LISTICLE;
            break;
          case 'landingImageTextTile':
          case 'landingImageSlider':
          case 'landingCategoryBanner':
            pageType = ClientPageType.LANDING;
            break;
          case 'ContentPage':
            pageType = ClientPageType.CONTENT;
            break;
          case 'giftFinderContent':
          case 'clothesFinderContent':
            pageType = ClientPageType.FINDER;
            break;
          default:
            pageType = null;
        }
      }
    },
  );

  return pageType;
};
